import { useTranslation } from "react-i18next";
import { Clock, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import {
  campaignAudienceActionBaseClass,
  campaignAudiencePrimaryActionClass,
} from "./CampaignManageRecipientsButton.js";
import { CampaignSendIcon } from "./CampaignSendIcon.js";

export function CampaignReviewActions({
  deliveryMode,
  executableCount,
  canSubmit,
  launching,
  canSendTest,
  launchHintId,
  onSubmit,
  onSendTest,
}: {
  deliveryMode: "now" | "schedule";
  executableCount: number;
  canSubmit: boolean;
  launching: boolean;
  canSendTest: boolean;
  launchHintId?: string;
  onSubmit: () => void;
  onSendTest: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const countLabel = new Intl.NumberFormat(i18n.language).format(executableCount);

  return (
    <div
      className="grid w-full min-w-0 grid-cols-1 gap-2 min-[420px]:grid-cols-[auto_minmax(0,1fr)] sm:flex sm:w-auto sm:items-center"
      data-testid="campaign-review-header-actions"
    >
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={cn(campaignAudienceActionBaseClass, "disabled:cursor-not-allowed")}
        disabled={!canSendTest}
        onClick={onSendTest}
        data-testid="campaign-send-test-open"
      >
        {t("campaigns.review.sendTest")}
      </Button>
      <Button
        type="button"
        size="sm"
        className={cn(
          campaignAudiencePrimaryActionClass,
          "min-w-0",
          launching ? null : "disabled:cursor-not-allowed",
        )}
        disabled={!canSubmit}
        aria-describedby={launchHintId}
        onClick={onSubmit}
        data-testid="campaign-launch-submit"
      >
        {launching ? (
          <>
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            <span className="truncate">{t("campaigns.review.launch.launching")}</span>
          </>
        ) : deliveryMode === "now" ? (
          <>
            <CampaignSendIcon />
            <span className="truncate">
              {t("campaigns.review.launch.sendToCount", { count: countLabel })}
            </span>
          </>
        ) : (
          <>
            <Clock size={14} aria-hidden="true" />
            <span className="truncate">
              {t("campaigns.review.launch.scheduleToCount", { count: countLabel })}
            </span>
          </>
        )}
      </Button>
    </div>
  );
}
